import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import SectionTitle from 'components/section-title'
import { Tab, Tabs } from 'components/tabs'
import { TonalButton } from 'components/empty-page-state'
import { DateTime } from 'luxon'
import NotificationsActiveIcon from 'components/material-symbol/icons/NotificationsActive'
import MessageOutlinedIcon from 'components/material-symbol/icons/Message'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import { FOLLOW_UP_LIST_PATH } from 'components/student-insights/constants'
import RetentionStatusChip from 'components/student-insights/retention-status-chip'
import { useStudentInsightsSummary } from 'components/student-insights/api-hooks'
import { followUpSearch } from 'components/student-insights/chat-follow-up-helper'
import { amplitudeEvent } from 'lib/amplitude'
import { useResizeObserver } from 'lib/hooks/use-resize-observer'
import { useAuth } from 'lib/auth'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { getInitials } from 'lib/text-display'
import { useGetLeads } from 'pages/pga-coach/leads/api-hooks'
import { isLeadContacted, isWaitlistLead } from 'pages/pga-coach/leads/utils'
import { formatLeadIntent } from 'pages/pga-coach/leads/lead-show-page/utils'
import {
  ATTENTION_ROW_HEIGHT,
  ATTENTION_ROW_HEIGHT_COMPACT,
  ATTENTION_ROW_LIMIT,
} from './attention-row-size'
import {
  AttentionListSkeleton,
  AttentionTabsSkeleton,
} from './loading-skeleton'
import useStyles from './styles'

export const ROW_LIMIT = ATTENTION_ROW_LIMIT
export const FOLLOW_UP_LIMIT = ROW_LIMIT
const MIN_FOLLOW_UP_ROWS = 2
const COMPACT_CARD_WIDTH = 520

const LEADS_PATH = '/pga-coach/leads'

const TABS = { ALL: 'all', LEADS: 'leads', FOLLOW_UPS: 'follow-ups' }

const fullName = ({ firstName, lastName }) =>
  [firstName, lastName].filter(Boolean).join(' ')

const leadItem = lead => ({
  key: `lead-${lead.id}`,
  type: 'lead',
  name: fullName(lead.contact),
  initials: getInitials(fullName(lead.contact)),
  meta: formatLeadIntent(lead.intent),
  receivedAt: lead.createdAt,
  unread: !lead.viewedAt,
  path: `${LEADS_PATH}/${lead.id}`,
  contactId: lead.contact.externalId,
})

const followUpItem = ({ student, status, reason }) => ({
  key: `student-${student.id}`,
  type: 'follow-up',
  name: fullName(student),
  initials: getInitials(fullName(student)),
  status,
  meta: reason,
  path: `/pga-coach/student/${student.id}`,
  contactId: student.contact?.externalId,
  search: followUpSearch(student.id),
})

const visibleItems = (tab, leads, followUps) => {
  if (tab === TABS.LEADS) return leads.slice(0, ROW_LIMIT)
  if (tab === TABS.FOLLOW_UPS) return followUps.slice(0, ROW_LIMIT)

  const followUpRows = Math.min(followUps.length, MIN_FOLLOW_UP_ROWS)
  return [...leads.slice(0, ROW_LIMIT - followUpRows), ...followUps].slice(
    0,
    ROW_LIMIT,
  )
}

const viewAllTarget = (tab, leads, followUps) => {
  if (tab === TABS.LEADS) return leads.length ? LEADS_PATH : null
  if (tab === TABS.FOLLOW_UPS) {
    return followUps.length ? FOLLOW_UP_LIST_PATH : null
  }
  if (leads.length && followUps.length) return null
  if (leads.length) return LEADS_PATH
  return followUps.length ? FOLLOW_UP_LIST_PATH : null
}

const EMPTY_MESSAGES = {
  [TABS.ALL]:
    "You're all caught up. New leads and students who cancel, are new, or haven't booked in a while show up here.",
  [TABS.LEADS]: 'No new leads.',
  [TABS.FOLLOW_UPS]:
    "You're all caught up. Students who cancel, are new, or haven't booked in a while show up here.",
}

const AttentionRow = ({ item, canMessage, compact }) => {
  const classes = useStyles()
  const history = useHistory()
  const openItem = () => history.push(item.path)
  const isLead = item.type === 'lead'
  const canSendMessage = item.contactId && (isLead || canMessage)

  return (
    <Box
      className={`${classes.row} ${classes.attentionRow} ${
        compact ? classes.attentionRowCompact : ''
      }`}
      onClick={openItem}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (event.target !== event.currentTarget) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          openItem()
        }
      }}
    >
      <Avatar
        className={`${classes.avatar} ${compact ? classes.avatarCompact : ''}`}
      >
        {item.initials}
      </Avatar>
      <Box className={classes.rowMain}>
        <Typography component="span" className={classes.rowName}>
          <span className={classes.rowNameText}>{item.name}</span>
          {isLead ? (
            <span className={classes.leadChip}>New Lead</span>
          ) : (
            <RetentionStatusChip status={item.status} />
          )}
          {item.unread ? (
            <span className={classes.unreadDot} aria-hidden />
          ) : null}
        </Typography>
        <Typography
          component="div"
          className={`${classes.rowMeta} ${
            compact ? classes.rowMetaClamped : classes.rowMetaSingleLine
          }`}
          title={item.meta}
        >
          {item.meta}
        </Typography>
        {isLead ? (
          <Typography component="div" className={classes.rowMeta}>
            Received {DateTime.fromISO(item.receivedAt).toRelative()}
          </Typography>
        ) : null}
      </Box>
      <Box className={classes.rowTrailing}>
        {canSendMessage ? (
          <StreamSendMessageButton
            userIds={[item.contactId]}
            search={item.search}
            component={isLead ? Button : TonalButton}
            variant="contained"
            color={isLead ? 'primary' : 'default'}
            size="medium"
            className={`${classes.replyButton} ${
              compact ? classes.replyButtonCompact : ''
            }`}
            startIcon={compact ? undefined : <MessageOutlinedIcon />}
            onClick={event => {
              event.stopPropagation()
              if (!isLead) {
                amplitudeEvent('student-insights-follow-up-opened', {
                  source: 'dashboard',
                })
              }
            }}
          >
            {compact ? (
              <MessageOutlinedIcon className={classes.compactButtonIcon} />
            ) : null}
            <span className={compact ? classes.visuallyHidden : undefined}>
              {isLead ? 'Reply' : 'Message'}
            </span>
          </StreamSendMessageButton>
        ) : null}
        {compact ? null : (
          <ChevronRightIcon className={classes.rowChevron} aria-hidden />
        )}
      </Box>
    </Box>
  )
}

AttentionRow.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.oneOf(['lead', 'follow-up']).isRequired,
    name: PropTypes.string,
    initials: PropTypes.string,
    status: PropTypes.string,
    meta: PropTypes.string,
    receivedAt: PropTypes.string,
    unread: PropTypes.bool,
    path: PropTypes.string.isRequired,
    contactId: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  canMessage: PropTypes.bool,
  compact: PropTypes.bool,
}

export const NeedsAttentionView = ({
  leads,
  followUps,
  followUpCount,
  showFollowUps,
  loading,
  tabsLoading,
  canMessage,
  className,
}) => {
  const classes = useStyles()
  const cardRef = useRef(null)
  const [compact, setCompact] = useState(false)
  const [selectedTab, setSelectedTab] = useState(TABS.ALL)
  const onResize = useCallback(
    entries => setCompact(entries[0].contentRect.width < COMPACT_CARD_WIDTH),
    [],
  )
  useResizeObserver({ refToObserve: cardRef, onResize })
  const tab = showFollowUps ? selectedTab : TABS.LEADS

  const totalCount = leads.length + followUpCount
  const items = visibleItems(tab, leads, followUps)
  const tallestTabRows = Math.max(
    ...(showFollowUps ? Object.values(TABS) : [TABS.LEADS]).map(
      name => visibleItems(name, leads, followUps).length,
    ),
  )
  const rowHeight = compact
    ? ATTENTION_ROW_HEIGHT_COMPACT
    : ATTENTION_ROW_HEIGHT
  const viewAllPath = viewAllTarget(tab, leads, followUps)

  return (
    <Box ref={cardRef} className={`${classes.card} ${className || ''}`}>
      <Box className={`${classes.cardHeader} ${classes.cardHeaderRoomy}`}>
        <Box className={classes.cardHeaderLeft}>
          <Box className={`${classes.iconCircle} ${classes.iconCircleLarge}`}>
            <NotificationsActiveIcon />
          </Box>
          <Box className={classes.cardHeading}>
            <SectionTitle className={classes.cardTitle}>
              Needs your attention
            </SectionTitle>
            <Typography className={classes.cardSubtitle}>
              Leads and students waiting on you
            </Typography>
          </Box>
        </Box>
        {viewAllPath ? (
          <Button
            component={RouterLink}
            to={viewAllPath}
            className={`${classes.cardAction} ${
              compact ? classes.cardActionCompact : ''
            }`}
            endIcon={compact ? undefined : <ChevronRightIcon />}
          >
            View all
          </Button>
        ) : (
          <Button
            component="span"
            aria-hidden
            className={`${classes.cardAction} ${
              compact ? classes.cardActionCompact : ''
            } ${classes.cardActionReserved}`}
            endIcon={compact ? undefined : <ChevronRightIcon />}
          >
            View all
          </Button>
        )}
      </Box>

      {showFollowUps ? (
        <Tabs
          value={tab}
          onChange={(event, value) => setSelectedTab(value)}
          insetLabels={false}
          variant={compact ? 'scrollable' : 'standard'}
          scrollButtons="off"
          className={`${classes.tabs} ${compact ? classes.tabsCompact : ''}`}
        >
          <Tab value={TABS.ALL} label={`All (${totalCount})`} />
          <Tab value={TABS.LEADS} label={`Leads (${leads.length})`} />
          <Tab
            value={TABS.FOLLOW_UPS}
            label={`Follow-ups (${followUpCount})`}
          />
        </Tabs>
      ) : tabsLoading ? (
        <AttentionTabsSkeleton compact={compact} />
      ) : null}

      <Box
        className={classes.attentionBody}
        style={{ minHeight: tallestTabRows * rowHeight }}
      >
        {loading ? (
          <AttentionListSkeleton compact={compact} />
        ) : items.length > 0 ? (
          <Box className={classes.list}>
            {items.map(item => (
              <AttentionRow
                key={item.key}
                item={item}
                canMessage={canMessage}
                compact={compact}
              />
            ))}
          </Box>
        ) : (
          <Typography
            className={`${classes.emptyList} ${classes.emptyListFill}`}
          >
            {EMPTY_MESSAGES[tab]}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

NeedsAttentionView.propTypes = {
  leads: PropTypes.arrayOf(PropTypes.object).isRequired,
  followUps: PropTypes.arrayOf(PropTypes.object).isRequired,
  followUpCount: PropTypes.number.isRequired,
  showFollowUps: PropTypes.bool,
  loading: PropTypes.bool,
  tabsLoading: PropTypes.bool,
  canMessage: PropTypes.bool,
  className: PropTypes.string,
}

NeedsAttentionView.defaultProps = {
  canMessage: true,
}

const useAwaitingLeads = () => {
  const { data, loading } = useGetLeads()

  const leads = (data?.coachingInquiries || [])
    .filter(lead => !isWaitlistLead(lead) && !isLeadContacted(lead))
    .sort(
      (a, b) =>
        DateTime.fromISO(b.createdAt).toMillis() -
        DateTime.fromISO(a.createdAt).toMillis(),
    )
    .map(leadItem)

  return { leads, loading: loading && !data }
}

const NeedsAttentionWithFollowUps = ({ className }) => {
  const { user } = useAuth()
  const { leads, loading: leadsLoading } = useAwaitingLeads()
  const { data, loading, error } = useStudentInsightsSummary(FOLLOW_UP_LIMIT)
  const summary = data?.studentInsightsSummary

  useEffect(() => {
    if (error) {
      window.rg4js &&
        window.rg4js('send', { error, tags: ['student-insights'] })
    }
  }, [error])

  return (
    <NeedsAttentionView
      className={className}
      leads={leads}
      followUps={(summary?.needsAttention || []).map(followUpItem)}
      followUpCount={summary?.needsAttentionCount || 0}
      showFollowUps={Boolean(summary)}
      loading={leadsLoading || (loading && !data)}
      tabsLoading={loading && !data}
      canMessage={!user?.hideSensitiveInformation}
    />
  )
}

const NeedsAttentionLeadsOnly = ({ className }) => {
  const { leads, loading } = useAwaitingLeads()

  return (
    <NeedsAttentionView
      className={className}
      leads={leads}
      followUps={[]}
      followUpCount={0}
      loading={loading}
    />
  )
}

const NeedsAttention = ({ className }) => {
  const [isStudentInsightsEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_STUDENT_INSIGHTS,
  ])

  return isStudentInsightsEnabled ? (
    <NeedsAttentionWithFollowUps className={className} />
  ) : (
    <NeedsAttentionLeadsOnly className={className} />
  )
}

NeedsAttention.propTypes = { className: PropTypes.string }
NeedsAttentionWithFollowUps.propTypes = { className: PropTypes.string }
NeedsAttentionLeadsOnly.propTypes = { className: PropTypes.string }

export default NeedsAttention
